'use client'

import { memo, type CSSProperties, type ReactNode } from 'react'

type GlassEffectProps = {
  children: ReactNode
  className?: string
  style?: CSSProperties
  href?: string
  target?: string
  role?: string
  'aria-live'?: 'off' | 'polite' | 'assertive'
}

export const NINXL_LIQUID_GLASS_FILTER_ID = 'ninxl-liquid-glass-distortion'

const joinClassNames = (...parts: Array<string | undefined>): string =>
  parts.filter(Boolean).join(' ')

const GlassEffectImpl = ({
  children,
  className = '',
  style,
  href,
  target = '_blank',
  role,
  'aria-live': ariaLive,
}: GlassEffectProps) => {
  const content = (
    <div
      className={joinClassNames('liquid-glass', className)}
      style={style}
      role={role}
      aria-live={ariaLive}
    >
      <div className="liquid-glass-distort" aria-hidden="true" />
      <div className="liquid-glass-tint" aria-hidden="true" />
      <div className="liquid-glass-shine" aria-hidden="true" />
      <div className="liquid-glass-content">{children}</div>
    </div>
  )

  if (!href) return content

  return (
    <a href={href} target={target} rel="noopener noreferrer" className="liquid-glass-link">
      {content}
    </a>
  )
}

export const GlassEffect = memo(GlassEffectImpl)

export const GlassFilter = memo(function GlassFilter() {
  return (
    <svg className="liquid-glass-filter" aria-hidden="true" focusable="false">
      <filter
        id={NINXL_LIQUID_GLASS_FILTER_ID}
        x="0%"
        y="0%"
        width="100%"
        height="100%"
        filterUnits="objectBoundingBox"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.001 0.005"
          numOctaves="1"
          seed="17"
          result="turbulence"
        />
        <feComponentTransfer in="turbulence" result="mapped">
          <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
          <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
          <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
        </feComponentTransfer>
        <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
        <feSpecularLighting
          in="softMap"
          surfaceScale="5"
          specularConstant="1"
          specularExponent="100"
          lightingColor="white"
          result="specLight"
        >
          <fePointLight x="-200" y="-200" z="300" />
        </feSpecularLighting>
        <feComposite
          in="specLight"
          operator="arithmetic"
          k1="0"
          k2="1"
          k3="1"
          k4="0"
          result="litImage"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="softMap"
          scale="48"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </svg>
  )
})
